import { LiveBidConsoleCatalog } from '@liveauctioneers/hammer-ui-liveauctioneers/desktopLiveBidPanel';
import { CurrencyCode } from '@liveauctioneers/hammer-ui-liveauctioneers/lotDetail';
import { useMemo } from 'react';

import { useAppSelector } from '@/redux/hooks';
import { getHasValidCardOnFile } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.selectors';
import { preferredCurrencySelector } from '@/redux/modules/bidder/preferences/bidderPreferences.selectors';
import {
    getCatalogCurrency,
    getCatalogIsLAPayments,
    getCatalogPaymentProvider,
    getCatalogStatus,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getCatalogNetworkStatus,
    getLiveCatalogClosed,
    getLiveCatalogStatus,
    getLiveCatalogWon,
} from '@/redux/modules/console';
import { getCurrencyConversion } from '@/redux/modules/currency';
import { getItemSummaryCatalogId } from '@/redux/modules/item/summary/itemSummary.selectors';
import { AuctionStatus } from '@/types/LiveAuctionState';

type Props = {
    itemId: number;
    selectedCurrency?: string;
};

export const useGetBiddingConsoleCatalogData = ({ itemId, selectedCurrency }: Props): LiveBidConsoleCatalog => {
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const currency = useAppSelector((state) => getCatalogCurrency(state, catalogId));
    const livePaymentsEnabled = useAppSelector((state) => getCatalogIsLAPayments(state, catalogId));
    const paymentProvider = useAppSelector((state) => getCatalogPaymentProvider(state, catalogId));
    const hasCreditCardOnFile = useAppSelector((state) => getHasValidCardOnFile(state, paymentProvider));

    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));
    const liveCatalogStatus = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const networkStatus = useAppSelector((state) => getCatalogNetworkStatus(state, catalogId));
    const isClosed = useAppSelector((state) => getLiveCatalogClosed(state, catalogId));
    const isWon = useAppSelector((state) => getLiveCatalogWon(state, catalogId));

    const preferredCurrency = useAppSelector(preferredCurrencySelector);

    // Use selectedCurrency if available, otherwise fall back to preferredCurrency
    const targetCurrency = selectedCurrency || preferredCurrency;
    const preferredCurrencyConversion = useAppSelector((state) => getCurrencyConversion(state, targetCurrency));
    const sourceCurrencyConversion = useAppSelector((state) => getCurrencyConversion(state, currency));

    const isAuctionLive =
        catalogStatus === AuctionStatus.Live &&
        liveCatalogStatus === AuctionStatus.Live &&
        networkStatus === 'connected' &&
        !isClosed &&
        !isWon;

    // Panel's auctionStatus is binary ('live' | 'paused').Treat as 'live' only when
    // the catalog is actually live, not closed/won, and the live socket is connected.
    // Anything else collapses to 'paused' so the panel disables its action button.
    const auctionStatus: LiveBidConsoleCatalog['auctionStatus'] = isAuctionLive
        ? AuctionStatus.Live
        : AuctionStatus.Paused;

    return useMemo(() => {
        return {
            auctionStatus,
            catalogId,
            currency: {
                hideConversionBadge: false,
                preferredCurrencyConversion,
                sourceCurrencyCode: currency.toUpperCase() as CurrencyCode,
                sourceCurrencyConversion,
            },
            livePayments: {
                hasCreditCardOnFile,
                livePaymentsEnabled,
            },
        };
    }, [
        catalogId,
        currency,
        hasCreditCardOnFile,
        livePaymentsEnabled,
        preferredCurrencyConversion,
        sourceCurrencyConversion,
        auctionStatus,
    ]);
};
